/**
 * ImageEditModal
 *
 * Rich image editing modal for the V4 CMS editor.
 * Four tabs: Upload, Gallery (with search), Pexels Search, Generate (AI).
 * SEO fields: alt text (required) and title (optional).
 *
 * When selecting from Pexels, the image is downloaded to R2 first
 * (no direct Pexels hotlinking). Alt text is auto-populated from
 * Pexels description or filename.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { previewImagePrompt, generateImage } from '../../api/editor';
import type { GenerationLevers, GenerateImageResponse } from '../../api/editor';
import {
  listMedia,
  uploadMedia,
  searchPexels,
  saveStockImage,
  type MediaItem,
  type PexelsPhoto,
} from '../../api/media';

// =============================================================================
// TYPES
// =============================================================================

export interface ImageEditModalProps {
  /** Current image URL */
  currentValue: string;
  /** Human-readable label (e.g. "Hero — Image") */
  label: string;
  /** Website ID for API calls */
  websiteId: string;
  /** Called with new image URL + alt text + framing when saved */
  onSave: (newUrl: string, altText: string, title: string, position: string) => void;
  /** Called when modal is closed without saving */
  onClose: () => void;
  /**
   * The slot's existing alt text, used to pre-seed the AI description.
   *
   * That descriptor is the most reliable artifact the site generator produces —
   * precise about what an image should show — so it is the right starting point
   * rather than a blank box.
   */
  currentAlt?: string;
  /**
   * 'add' turns the modal into the gallery add-photo flow: the Upload tab
   * multi-selects and every uploaded file is handed straight to `onAddMany` —
   * no per-file save step. A single pick from Gallery/Pexels/Generate still
   * goes through Save/onSave, so alt, caption and framing can be set on the
   * way in. Default 'replace' is the unchanged existing behaviour.
   */
  mode?: 'replace' | 'add';
  onAddMany?: (items: Array<{ url: string; alt: string }>) => void;
  /**
   * The slot's existing second text field, if the caller stores one.
   *
   * A gallery image keeps a caption that renders on the picture; other slots
   * have only a tooltip title. The caller names the field so the modal is not
   * quietly writing a caption into something labelled "tooltip".
   */
  currentTitle?: string;
  titleLabel?: string;
  titleHint?: string;
  /**
   * The slot's existing `object-position`, as "X% Y%".
   *
   * Absent means the browser default of "50% 50%" — the centre crop every
   * image has had until now — so an unframed image opens where it already sits
   * rather than jumping.
   */
  currentPosition?: string;
  /**
   * Show the framing control.
   *
   * Off by default because framing is persisted under a suffixed element id,
   * which only a caller wired to the staging element API can send. EditPanel
   * renders this modal through a plain `onChange(url)` and has no such path —
   * showing the control there would let staff reframe an image and lose it on
   * save, silently. It appears only where it can actually be stored.
   */
  enableFraming?: boolean;
}

/** Centre crop: what `object-cover` does with no `object-position` set. */
const DEFAULT_POSITION = '50% 50%';

/** Parse "X% Y%" into numbers for the crosshair, tolerating anything odd. */
function parsePosition(value: string): { x: number; y: number } {
  const [rawX, rawY] = value.split(/\s+/);
  const x = Number.parseFloat(rawX);
  const y = Number.parseFloat(rawY);
  return {
    x: Number.isFinite(x) ? Math.min(100, Math.max(0, x)) : 50,
    y: Number.isFinite(y) ? Math.min(100, Math.max(0, y)) : 50,
  };
}

type Tab = 'upload' | 'gallery' | 'pexels' | 'generate';

/**
 * Gallery badge per media source.
 *
 * This was `source === 'upload' ? 'Upload' : 'Pexels'` — a binary written
 * before `unsplash` and `generated` existed, so every non-upload image
 * claimed to be from Pexels. AI-generated images showed a Pexels badge,
 * which misstates both their provenance and their licensing.
 *
 * Keyed off MediaSource so a new source shows its own name rather than
 * silently inheriting someone else's.
 */
const SOURCE_BADGE: Record<string, { label: string; className: string }> = {
  upload: { label: 'Upload', className: 'bg-green-100 text-green-700' },
  pexels: { label: 'Pexels', className: 'bg-purple-100 text-purple-700' },
  unsplash: { label: 'Unsplash', className: 'bg-sky-100 text-sky-700' },
  generated: { label: 'AI', className: 'bg-amber-100 text-amber-700' },
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function ImageEditModal({
  currentValue,
  label,
  websiteId,
  onSave,
  onClose,
  currentAlt,
  mode = 'replace',
  onAddMany,
  currentTitle,
  titleLabel = 'Title',
  titleHint = '(tooltip)',
  currentPosition,
  enableFraming = false,
}: ImageEditModalProps) {
  const [activeTab, setActiveTab] = useState<Tab>('gallery');
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  // Seeded from the slot's stored values. Opening blank made the modal look
  // like a second, competing source of truth beside the panel's own fields.
  const [altText, setAltText] = useState(currentAlt || '');
  const [titleText, setTitleText] = useState(currentTitle || '');
  const [saving, setSaving] = useState(false);

  // Track a pending Pexels photo that hasn't been saved to R2 yet
  const [pendingPexels, setPendingPexels] = useState<{
    photo: PexelsPhoto;
    query: string;
  } | null>(null);

  // Framing. `object-position` reframes within the existing frame; it does not
  // crop the asset, so it is free to change and free to undo.
  const initialPosition = currentPosition || DEFAULT_POSITION;
  const [position, setPosition] = useState(initialPosition);
  const [draggingFocus, setDraggingFocus] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  const displayUrl = selectedUrl || currentValue;
  // Reframing alone is a change worth saving — the Save button must not stay
  // disabled just because the image itself was left as it was.
  const hasChanges = selectedUrl !== null || (enableFraming && position !== initialPosition);

  const focus = parsePosition(position);

  const setFocusFromPointer = useCallback((clientX: number, clientY: number) => {
    const el = previewRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100));
    setPosition(`${x.toFixed(1)}% ${y.toFixed(1)}%`);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && hasChanges && !saving) {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, hasChanges, saving]);

  const handleSelect = useCallback((url: string, autoAlt?: string) => {
    setSelectedUrl(url);
    setPendingPexels(null); // Clear any pending Pexels photo (this is a gallery/upload selection)
    if (autoAlt) {
      setAltText((prev) => prev || autoAlt);
    }
  }, []);

  // Called when a Pexels photo is clicked — preview only, don't save to R2 yet
  const handlePexelsPreview = useCallback((photo: PexelsPhoto, query: string) => {
    setSelectedUrl(photo.src.large || photo.src.medium);
    setPendingPexels({ photo, query });
    setAltText((prev) => prev || photo.alt || query);
  }, []);

  const handleSave = useCallback(async () => {
    if (!hasChanges || saving) return;

    // If a Pexels photo is pending, save to R2 first
    if (pendingPexels) {
      setSaving(true);
      try {
        const result = await saveStockImage(websiteId, pendingPexels.photo, pendingPexels.query);
        onSave(result.media.url, altText, titleText, position);
      } catch (err) {
        console.error('Failed to save Pexels image:', err);
        // Fall back to the preview URL (Pexels direct) if R2 save fails
        onSave(displayUrl, altText, titleText, position);
      } finally {
        setSaving(false);
      }
    } else {
      // Gallery or upload selection — URL is already an R2 URL
      onSave(displayUrl, altText, titleText, position);
    }
  }, [hasChanges, saving, pendingPexels, websiteId, displayUrl, altText, titleText, position, onSave]);

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 overflow-hidden max-h-[88vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center text-sm">
              {'\u{1F5BC}'}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{label}</h3>
              <p className="text-xs text-gray-400">Select or upload an image</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Two-column body */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left column: Preview + SEO fields */}
          <div className="w-[340px] shrink-0 border-r border-gray-100 flex flex-col overflow-y-auto">
            <div className="p-4">
              {/*
                The preview is deliberately a fixed 4:3 crop rather than the
                image's own aspect. object-position only does anything when the
                image is actually being cropped, so a preview that shows the
                whole photo would make the control look broken — you would drag
                and nothing would move.
              */}
              <div
                ref={previewRef}
                className={`relative rounded-lg overflow-hidden bg-gray-50 border border-gray-200 aspect-[4/3] ${
                  displayUrl && enableFraming ? 'cursor-crosshair touch-none select-none' : ''
                }`}
                onPointerDown={(e) => {
                  if (!displayUrl || !enableFraming) return;
                  e.currentTarget.setPointerCapture(e.pointerId);
                  setDraggingFocus(true);
                  setFocusFromPointer(e.clientX, e.clientY);
                }}
                onPointerMove={(e) => {
                  if (!draggingFocus) return;
                  setFocusFromPointer(e.clientX, e.clientY);
                }}
                onPointerUp={(e) => {
                  if (!draggingFocus) return;
                  e.currentTarget.releasePointerCapture(e.pointerId);
                  setDraggingFocus(false);
                }}
              >
                {displayUrl ? (
                  <img
                    src={displayUrl}
                    alt={altText || 'Preview'}
                    className="w-full h-full object-cover pointer-events-none"
                    style={{ objectPosition: position }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                {displayUrl && enableFraming && (
                  <div
                    className="absolute w-6 h-6 -ml-3 -mt-3 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.35)] pointer-events-none"
                    style={{ left: `${focus.x}%`, top: `${focus.y}%` }}
                  >
                    <div className="absolute inset-1.5 rounded-full bg-white/90" />
                  </div>
                )}
                {selectedUrl && (
                  <div className="absolute top-2 right-2 px-2 py-0.5 bg-yellow-400 text-yellow-900 text-xs font-medium rounded">
                    New
                  </div>
                )}
              </div>
              {displayUrl && enableFraming && (
                <div className="flex items-center justify-between mt-1.5">
                  <p className="text-xs text-gray-400">
                    Drag to reframe · {focus.x.toFixed(0)}% {focus.y.toFixed(0)}%
                  </p>
                  <button
                    type="button"
                    onClick={() => setPosition(DEFAULT_POSITION)}
                    disabled={position === DEFAULT_POSITION}
                    className={`text-xs transition-colors ${
                      position === DEFAULT_POSITION
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-blue-600 hover:text-blue-700'
                    }`}
                  >
                    Center
                  </button>
                </div>
              )}
            </div>
            <div className="px-4 pb-4 pt-2 space-y-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Alt Text <span className="text-gray-400">(SEO)</span>
                </label>
                <input
                  type="text"
                  value={altText}
                  onChange={(e) => setAltText(e.target.value)}
                  placeholder="Describe this image..."
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {titleLabel} <span className="text-gray-400">{titleHint}</span>
                </label>
                <input
                  type="text"
                  value={titleText}
                  onChange={(e) => setTitleText(e.target.value)}
                  placeholder={`Optional ${titleLabel.toLowerCase()}...`}
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
                />
              </div>
            </div>
          </div>

          {/* Right column: Tabs + content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex gap-1 px-4 pt-3 pb-2 shrink-0">
              <TabButton tab="upload" activeTab={activeTab} label="Upload" onClick={() => setActiveTab('upload')} icon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              } />
              <TabButton tab="gallery" activeTab={activeTab} label="Gallery" onClick={() => setActiveTab('gallery')} icon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              } />
              <TabButton tab="generate" activeTab={activeTab} label="Generate" onClick={() => setActiveTab('generate')} icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              } />
              <TabButton tab="pexels" activeTab={activeTab} label="Stock" onClick={() => setActiveTab('pexels')} icon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              } />
            </div>
            <div className="px-4 pb-3 flex-1 overflow-y-auto">
              {activeTab === 'upload' && (
                <UploadTab
                  websiteId={websiteId}
                  onSelect={handleSelect}
                  onSelectMany={mode === 'add' ? onAddMany : undefined}
                />
              )}
              {activeTab === 'gallery' && (
                <GalleryTab websiteId={websiteId} onSelect={handleSelect} />
              )}
              {activeTab === 'generate' && (
                <GenerateTab
                  websiteId={websiteId}
                  seedIntent={currentAlt || label}
                  referenceCandidate={displayUrl}
                  onSelect={handleSelect}
                />
              )}
              {activeTab === 'pexels' && (
                <PexelsTab websiteId={websiteId} onPreview={handlePexelsPreview} />
              )}
            </div>
          </div>
        </div>

        {/* Footer — full width */}
        <div className="flex items-center justify-between px-5 py-2.5 border-t border-gray-100 bg-gray-50 shrink-0">
          <p className="text-xs text-gray-400">
            {hasChanges ? '\u2318+Enter to save' : 'Select an image to get started'}
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-3.5 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className={`px-3.5 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                hasChanges && !saving
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <Spinner size="sm" color="white" />
                  Saving…
                </span>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// TAB BUTTON
// =============================================================================

function TabButton({
  tab,
  activeTab,
  label,
  icon,
  onClick,
}: {
  tab: Tab;
  activeTab: Tab;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  const isActive = tab === activeTab;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
        isActive
          ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200'
          : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

// =============================================================================
// UPLOAD TAB
// =============================================================================

function UploadTab({
  websiteId,
  onSelect,
  onSelectMany,
}: {
  websiteId: string;
  onSelect: (url: string, autoAlt?: string) => void;
  /**
   * When set, the file input allows multi-select and every valid file is
   * uploaded in turn — one gallery slot per photo, one picker visit total.
   * Absent (the replace flow), behaviour is unchanged single-file.
   */
  onSelectMany?: (items: Array<{ url: string; alt: string }>) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const altFromName = (name: string) =>
    name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ').replace(/\s+/g, ' ').trim();

  const handleUploadMany = async (files: File[]) => {
    const valid = files.filter((f) => f.type.startsWith('image/') && f.size <= 10 * 1024 * 1024);
    const skipped = files.length - valid.length;
    if (valid.length === 0) {
      setError('No usable images — files must be images under 10MB.');
      return;
    }
    setUploading(true);
    setError(null);
    const items: Array<{ url: string; alt: string }> = [];
    try {
      for (let i = 0; i < valid.length; i++) {
        setProgress(`Uploading ${i + 1} of ${valid.length}…`);
        const result = await uploadMedia(websiteId, valid[i]);
        items.push({ url: result.media.url, alt: altFromName(valid[i].name) });
      }
      if (skipped > 0) setError(`${skipped} file${skipped === 1 ? ' was' : 's were'} skipped (not an image, or over 10MB).`);
      onSelectMany?.(items);
    } catch (err) {
      // Photos uploaded before the failure are not lost — hand them over.
      if (items.length > 0) onSelectMany?.(items);
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(null);
    }
  };

  const handleUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File must be less than 10MB');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const result = await uploadMedia(websiteId, file);
      // Auto-populate alt text from filename (strip extension, replace separators)
      const cleanName = file.name
        .replace(/\.[^.]+$/, '')
        .replace(/[-_]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      onSelect(result.media.url, cleanName);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;
    if (onSelectMany) handleUploadMany(files);
    else handleUpload(files[0]);
  };

  return (
    <div className="space-y-3">
      <div
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
          dragOver
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          multiple={Boolean(onSelectMany)}
          onChange={(e) => {
            const files = e.target.files ? Array.from(e.target.files) : [];
            if (files.length === 0) return;
            if (onSelectMany) handleUploadMany(files);
            else handleUpload(files[0]);
          }}
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            {progress && <p className="text-xs text-gray-500">{progress}</p>}
            <Spinner />
            <p className="text-sm text-gray-600">Uploading...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-sm text-gray-600">
              <span className="text-blue-600 font-medium">Click to upload</span> or drag and drop
            </p>
            <p className="text-xs text-gray-400">PNG, JPG, WebP or GIF up to 10MB</p>
          </div>
        )}
      </div>
      {error && <ErrorMessage message={error} />}
    </div>
  );
}

// =============================================================================
// GALLERY TAB (with search filter)
// =============================================================================

function GalleryTab({
  websiteId,
  onSelect,
}: {
  websiteId: string;
  onSelect: (url: string, autoAlt?: string) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    listMedia(websiteId)
      .then((data) => {
        setMedia(data.media);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [websiteId]);

  // Client-side filter by alt text, title, filename, or original query
  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return media;
    const q = searchQuery.toLowerCase();
    return media.filter((item) => {
      const fields = [
        item.alt_text,
        item.title,
        item.original_filename,
        item.original_query,
      ];
      return fields.some((f) => f && f.toLowerCase().includes(q));
    });
  }, [media, searchQuery]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  if (media.length === 0) {
    return (
      <div className="text-center py-10">
        <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <p className="text-gray-500 text-sm">No images in your library yet</p>
        <p className="text-xs text-gray-400 mt-1">Upload an image or save from Pexels to get started</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Search filter */}
      <div className="relative">
        <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Filter by name, alt text, or keyword..."
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
        />
      </div>

      {/* Image grid */}
      {filtered.length === 0 ? (
        <p className="text-center text-gray-400 text-sm py-6">No images match "{searchQuery}"</p>
      ) : (
        <div className="grid grid-cols-3 gap-3 max-h-[280px] overflow-y-auto p-0.5">
          {filtered.map((item) => (
            <button
              key={item.id}
              onClick={() => onSelect(item.url, item.alt_text || item.original_filename || undefined)}
              className="group relative h-[100px] rounded-lg overflow-hidden border-2 border-transparent hover:border-blue-500 transition-all"
              title={item.alt_text || item.original_filename || undefined}
            >
              <img
                src={item.thumbnailUrl || item.url}
                alt={item.alt_text || 'Media'}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <span className="text-white text-xs font-medium">Select</span>
              </div>
              {/* Source badge */}
              <div className="absolute bottom-1 left-1">
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${SOURCE_BADGE[item.source]?.className ?? SOURCE_BADGE.pexels.className}`}>
                  {SOURCE_BADGE[item.source]?.label ?? item.source}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      <p className="text-xs text-gray-400 text-right">
        {filtered.length} of {media.length} images
      </p>
    </div>
  );
}

// =============================================================================
// PEXELS TAB
// =============================================================================

function PexelsTab({
  websiteId,
  onPreview,
}: {
  websiteId: string;
  onPreview: (photo: PexelsPhoto, query: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [photos, setPhotos] = useState<PexelsPhoto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const handleSearch = useCallback(async (resetPage = true) => {
    if (!query.trim()) return;
    const searchPage = resetPage ? 1 : page + 1;
    if (resetPage) {
      setLoading(true);
      setPhotos([]);
    } else {
      setLoadingMore(true);
    }
    setError(null);
    try {
      const result = await searchPexels(websiteId, query, { page: searchPage, perPage: 20 });
      if (resetPage) {
        setPhotos(result.photos);
      } else {
        setPhotos((prev) => [...prev, ...result.photos]);
      }
      setPage(searchPage);
      setHasMore(searchPage * 20 < result.total_results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [websiteId, query, page]);

  return (
    <div className="space-y-3">
      {/* Search bar */}
      <div className="flex gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search free stock photos..."
          className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
          onKeyDown={(e) => e.key === 'Enter' && handleSearch(true)}
        />
        <button
          type="button"
          onClick={() => handleSearch(true)}
          disabled={loading || !query.trim()}
          className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? <Spinner size="sm" /> : 'Search'}
        </button>
      </div>

      {error && <ErrorMessage message={error} />}

      {/* Results */}
      {loading && photos.length === 0 ? (
        <LoadingSpinner />
      ) : photos.length > 0 ? (
        <div className="max-h-[280px] overflow-y-auto p-0.5 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            {photos.map((photo) => (
              <button
                key={photo.id}
                onClick={() => onPreview(photo, query)}
                className="group relative h-[100px] rounded-lg overflow-hidden border-2 border-transparent hover:border-blue-500 transition-all"
              >
                <img
                  src={photo.src.medium}
                  alt={photo.alt || 'Stock photo'}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-white text-xs font-medium">Preview</span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-1.5 bg-gradient-to-t from-black/70 to-transparent">
                  <p className="text-white text-[10px] truncate">by {photo.photographer}</p>
                </div>
              </button>
            ))}
          </div>
          {hasMore && (
            <div className="text-center pt-1">
              <button
                type="button"
                onClick={() => handleSearch(false)}
                disabled={loadingMore}
                className="px-4 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
              >
                {loadingMore ? (
                  <span className="flex items-center gap-2 justify-center">
                    <Spinner size="sm" /> Loading...
                  </span>
                ) : (
                  'Load more results'
                )}
              </button>
            </div>
          )}
        </div>
      ) : !loading && query ? (
        <p className="text-center text-gray-400 text-sm py-8">No images found. Try a different search term.</p>
      ) : (
        <div className="text-center py-8">
          <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p className="text-gray-500 text-sm">Search for free stock photos</p>
          <p className="text-xs text-gray-400 mt-1">Powered by Pexels — saved to your library</p>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// SHARED UI HELPERS
// =============================================================================

function Spinner({ size = 'md', color = 'blue' }: { size?: 'sm' | 'md'; color?: 'blue' | 'white' }) {
  const sizeClass = size === 'sm' ? 'w-5 h-5' : 'w-8 h-8';
  const colorClass = color === 'white' ? 'text-white' : 'text-blue-500';
  return (
    <svg className={`${sizeClass} ${colorClass} animate-spin`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-10">
      <Spinner />
    </div>
  );
}

function ErrorMessage({ message }: { message: string }) {
  return (
    <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
      {message}
    </div>
  );
}

// =============================================================================
// GENERATE TAB — AI image generation
// =============================================================================

/**
 * For the slots stock cannot serve.
 *
 * The interface is deliberately layered so it stays usable without becoming a
 * prompt console:
 *
 *  1. A plain description, pre-seeded from the slot's existing alt text. Most
 *     of the time this alone is enough — the client's own visual style is
 *     applied server-side, and that is what makes a site's images cohere.
 *  2. Four optional selectors for precision.
 *  3. The assembled prompt, collapsed by default. Available when someone wants
 *     to see exactly what was sent, hidden when they don't — showing it up
 *     front invites editing toward longer prompts, and past a point every
 *     extra clause dilutes the subject.
 */
function GenerateTab({
  websiteId,
  seedIntent,
  referenceCandidate,
  onSelect,
}: {
  websiteId: string;
  seedIntent: string;
  /** The image currently shown — offered as an optional reference. */
  referenceCandidate?: string;
  onSelect: (url: string, autoAlt?: string) => void;
}) {
  const [intent, setIntent] = useState(seedIntent);
  const [levers, setLevers] = useState<GenerationLevers>({});
  const [useReference, setUseReference] = useState(false);
  const [permissionConfirmed, setPermissionConfirmed] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [preview, setPreview] = useState<GenerateImageResponse | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GenerateImageResponse | null>(null);

  const referenceUrl = useReference ? referenceCandidate : undefined;
  const blockedOnPermission = !!referenceUrl && !permissionConfirmed;
  const canGenerate = intent.trim().length > 0 && !generating && !blockedOnPermission;

  // Fetch the assembled prompt only while it is on screen. Debounced so typing
  // does not fire a request per keystroke.
  useEffect(() => {
    if (!showPrompt || !intent.trim()) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const p = await previewImagePrompt(websiteId, {
          intent: intent.trim(),
          levers,
          referenceUrl,
        });
        if (!cancelled) setPreview(p);
      } catch {
        // Preview is a convenience; a failure here must not block generating.
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [websiteId, intent, levers, referenceUrl, showPrompt]);

  const handleGenerate = useCallback(async () => {
    if (!canGenerate) return;
    setGenerating(true);
    setError(null);
    setResult(null);
    try {
      const r = await generateImage(websiteId, {
        intent: intent.trim(),
        levers,
        referenceUrl,
        referencePermissionConfirmed: referenceUrl ? permissionConfirmed : undefined,
      });
      if (!r.success) {
        setError(r.error || 'Generation failed');
        return;
      }
      setResult(r);
      onSelect(r.url!, intent.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  }, [canGenerate, websiteId, intent, levers, referenceUrl, permissionConfirmed, onSelect]);

  const setLever = (key: keyof GenerationLevers, value: string) =>
    setLevers((prev) => ({ ...prev, [key]: value || undefined }));

  return (
    <div className="p-4 space-y-4">
      {/* 1. The description — the primary control */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Describe the image
        </label>
        <textarea
          value={intent}
          onChange={(e) => setIntent(e.target.value)}
          rows={2}
          placeholder="Women gathered in a living room for Bible study, laughing together"
          className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-lg resize-none focus:outline-none focus:border-blue-400"
        />
        <p className="mt-1 text-xs text-gray-400">
          Pre-filled from this image's description. The client's brand style is applied automatically.
        </p>
      </div>

      {/* 2. Optional precision */}
      <div className="grid grid-cols-2 gap-2">
        <LeverSelect label="Setting" value={levers.setting} onChange={(v) => setLever('setting', v)}
          options={[['home','Home'],['outdoor','Outdoors'],['studio','Studio'],['workplace','Workplace'],['community-space','Community space']]} />
        <LeverSelect label="Mood" value={levers.mood} onChange={(v) => setLever('mood', v)}
          options={[['warm-candid','Warm & candid'],['bright-energetic','Bright & energetic'],['calm-serene','Calm & serene'],['professional','Professional']]} />
        <LeverSelect label="Framing" value={levers.shot} onChange={(v) => setLever('shot', v)}
          options={[['close-up','Close-up'],['medium','Medium shot'],['wide','Wide shot']]} />
        <LeverSelect label="People" value={levers.people} onChange={(v) => setLever('people', v)}
          options={[['none','No people'],['one','One person'],['pair','Two people'],['small-group','Small group']]} />
      </div>

      {levers.people && levers.people !== 'none' && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Who should this show? <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <input
            value={levers.representation || ''}
            onChange={(e) => setLever('representation', e.target.value)}
            placeholder="e.g. predominantly Black women, suburban Georgia"
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400"
          />
          <p className="mt-1 text-xs text-gray-400">
            Set this from what the client tells you about their community — it isn't inferred.
          </p>
        </div>
      )}

      {/* 3. Reference image */}
      {referenceCandidate && (
        <div className="border border-gray-200 rounded-lg p-2.5">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={useReference}
              onChange={(e) => { setUseReference(e.target.checked); if (!e.target.checked) setPermissionConfirmed(false); }}
              className="rounded border-gray-300" />
            <span className="text-sm text-gray-800">Build on the current image</span>
          </label>

          {useReference && (
            <div className="mt-2.5 flex gap-3">
              <img src={referenceCandidate} alt="" className="w-16 h-16 object-cover rounded border border-gray-200 shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-2">
                  Keeps the people and composition, changes what you describe — a different setting, or
                  the same scene without an unwanted detail.
                </p>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={permissionConfirmed}
                    onChange={(e) => setPermissionConfirmed(e.target.checked)}
                    className="mt-0.5 rounded border-gray-300" />
                  <span className="text-xs text-gray-700">
                    I have permission to use this image as an AI reference.
                    <span className="block text-gray-400 mt-0.5">
                      Stock licences often prohibit it, and a photo of a real person needs that person's agreement.
                    </span>
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 4. The assembled prompt — on demand */}
      <div>
        <button onClick={() => setShowPrompt((v) => !v)}
          className="text-xs text-gray-500 hover:text-gray-800 underline underline-offset-2">
          {showPrompt ? 'Hide' : 'See'} the full prompt
        </button>
        {showPrompt && (
          <div className="mt-2 p-2.5 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
            {preview?.prompt ? (
              <>
                <p className="font-mono leading-relaxed">{preview.prompt}</p>
                {preview.leversApplied && preview.leversApplied.length > 0 && (
                  <p className="mt-2 text-gray-400">Applied: {preview.leversApplied.join(', ')}</p>
                )}
              </>
            ) : (
              <span className="text-gray-400">Building…</span>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="px-2.5 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">{error}</div>
      )}

      {result?.url && (
        <div className="px-2.5 py-2 bg-green-50 border border-green-200 rounded text-xs text-green-800">
          Generated{result.fromCache ? ' (reused an identical brief — no charge)' : ` for $${result.costUsd?.toFixed(3)}`}.
          Selected on the left — press Save to apply.
        </div>
      )}

      <button onClick={() => void handleGenerate()} disabled={!canGenerate}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-40">
        {generating ? 'Generating…' : `Generate image · ~$${(preview?.estimatedCostUsd ?? 0.013).toFixed(3)}`}
      </button>
      {blockedOnPermission && (
        <p className="text-xs text-amber-700 -mt-2">Confirm permission above to use the reference image.</p>
      )}
    </div>
  );
}

function LeverSelect({
  label, value, onChange, options,
}: {
  label: string;
  value?: string;
  onChange: (v: string) => void;
  options: Array<[string, string]>;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <select value={value || ''} onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-blue-400">
        <option value="">Auto</option>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
